import { useState, type ReactNode } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ShieldCheck, Package, AlertTriangle, Pencil, Zap, Car, Receipt, FileText, ClipboardList, ExternalLink } from 'lucide-react'
import { ConsumerShell } from '@/consumer/components/consumer-shell'
import { useConsumerHolding } from '@/consumer/hooks/use-holdings'
import { useConsumerClaims } from '@/consumer/hooks/use-consumer-claims'
import { useConsumerClaimsEnabled } from '@/consumer/hooks/use-consumer-claims-enabled'
import { formatDate as fmtDate } from '@/admin/lib/formatting'
import { useFormattingContext } from '@/shared/lib/formatting'
import { formatDisplayValue, defineField } from '@/shared/lib/forms'
import { DetailCard, FieldRow } from '@/admin/components/shared/detail-card'
import { Button } from '@/shared/components/ui/button'
import { Image } from '@/shared/components/ui/image'
import { CertificateViewerDialog } from '@/shared/components/certificate-viewer-dialog'
import api from '@/consumer/lib/api'

import { toastApiError } from '@/shared/lib/api-error'

function makeValueFormatters(locale: string, currency: string) {
  function formatDate(iso: string | null | undefined): string {
    if (!iso) return '-'
    return fmtDate(String(iso), locale)
  }
  // Delegate to the shared display formatter so every field type - including
  // FILE / IMAGE (rendered as a link), MONEY, DATE, BOOLEAN and ADDRESS - is
  // formatted consistently. Previously a file/image value (e.g. Proof of
  // Purchase) fell through to String(value) and rendered as "[object Object]".
  function formatValue(value: unknown, fieldType: string): ReactNode {
    return formatDisplayValue(
      defineField({ code_name: '', display_name: '', field_type: fieldType }),
      value,
      { locale, currency },
    )
  }
  return { formatDate, formatValue }
}

export default function ProductDetailPage() {
  const { t } = useTranslation()
  const { locale, currency } = useFormattingContext()
  const { formatDate, formatValue } = makeValueFormatters(locale, currency)
  const { uuid } = useParams<{ uuid: string }>()
  const navigate = useNavigate()
  const { data: holding, isLoading, isError } = useConsumerHolding(uuid)
  const { data: allClaims } = useConsumerClaims()
  const claimsEnabled = useConsumerClaimsEnabled()
  const holdingClaims = (allClaims ?? []).filter((c) => c.product_name === holding?.product?.name)
  const [certOpen, setCertOpen] = useState(false)
  const [certHtml, setCertHtml] = useState<string | null>(null)

  async function handleViewCertificate() {
    setCertOpen(true)
    if (certHtml) return // already loaded
    try {
      const res = await api.get(`/holdings/${uuid}/certificate/`)
      setCertHtml(res.data.html)
    } catch (err) {
      toastApiError(err, 'Failed to load certificate');
      setCertOpen(false)
    }
  }

  if (isLoading) {
    return (
      <ConsumerShell>
        <div className="flex items-center justify-center py-20">
          <p className="text-[13px] text-[var(--ink-3)]">Loading…</p>
        </div>
      </ConsumerShell>
    );
  }

  if (isError || !holding) {
    return (
      <ConsumerShell>
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <p className="text-[13px] text-[var(--ink-3)]">Product not found</p>
          <Button
            variant="link"
            onClick={() => navigate('/')}
            className="text-[11px] underline cursor-pointer text-[var(--green)]"
          >
            Back to home
          </Button>
        </div>
      </ConsumerShell>
    );
  }

  const warranty = holding.warranty
  const isActive = warranty?.status === 'ACTIVE'

  return (
    <ConsumerShell>
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/')}
        className="mb-8 flex items-center gap-1.5 rounded-[4px] px-2 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)] transition-colors cursor-pointer hover:bg-[var(--bg-subtle)]"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back
      </Button>
      {/* Product header */}
      <div
        className="mb-6 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-6"
      >
        <div className="flex gap-6">
          {/* Image */}
          <Image
            src={holding.product.image_url}
            alt={holding.product.name}
            className="h-24 w-24 shrink-0 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]"
            fallback={<Package className="h-10 w-10 text-[var(--ink-3)]" />}
          />

          {/* Title + meta */}
          <div className="flex-1">
            <h1 className="clara-heading mb-1">{holding.product.name}</h1>
            <p className="mb-3 text-[12px] text-[var(--ink-3)]">
              {holding.product.template_name || '-'}
              {holding.host?.registration_number && (
                <> &middot; <span className="font-medium tracking-wide">{holding.host.registration_number}</span></>
              )}
              {(() => {
                const make = holding.host?.attribute_values?.find((a) => a.code_name === 'make')?.value
                return make ? <> &middot; {String(make)}</> : null
              })()}
            </p>
            <div className="flex items-center gap-3">
              {holding.organisation.logo_url ? (
                <img
                  src={holding.organisation.logo_url}
                  alt={holding.organisation.name}
                  className="h-4 max-w-[80px] object-contain opacity-60"
                />
              ) : (
                <span className="text-[11px] text-[var(--ink-3)]">
                  {holding.organisation.name}
                </span>
              )}
              <span className="text-[11px] text-[var(--ink-3)]">
                Registered {formatDate(holding.created_at)}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Product information - 3 cols */}
        <div className="lg:col-span-3 flex flex-col gap-6">
          {holding.attributes && holding.attributes.length > 0 && (
            <DetailCard
              title="Product Information"
              icon={<Package className="h-3.5 w-3.5" />}
              collapsible={false}
            >
              {holding.attributes.map((attr) => (
                <FieldRow
                  key={attr.code_name}
                  label={attr.display_name}
                  value={formatValue(attr.value, attr.field_type)}
                />
              ))}
            </DetailCard>
          )}

          {/* Host information (vehicle / property) */}
          {holding.host && holding.host.attribute_values && holding.host.attribute_values.length > 0 && (
            <DetailCard
              title={holding.host.kind === 'VEHICLE' ? 'Vehicle Details' : 'Host Information'}
              icon={<Car className="h-3.5 w-3.5" />}
              collapsible={false}
            >
              {holding.host.attribute_values.map((attr: { code_name: string; display_name: string; value: unknown; field_type: string }) => (
                <FieldRow
                  key={attr.code_name}
                  label={attr.display_name}
                  value={formatValue(attr.value, attr.field_type)}
                />
              ))}
            </DetailCard>
          )}

          {/* Transaction / purchase details */}
          {holding.transaction_attributes && holding.transaction_attributes.length > 0 && (
            <DetailCard
              title="Purchase Details"
              icon={<Receipt className="h-3.5 w-3.5" />}
              collapsible={false}
            >
              {holding.transaction_attributes.map((attr: { code_name: string; display_name: string; value: unknown; field_type: string }) => (
                <FieldRow
                  key={attr.code_name}
                  label={attr.display_name}
                  value={formatValue(attr.value, attr.field_type)}
                />
              ))}
            </DetailCard>
          )}
        </div>

        {/* Warranty + Actions - 2 cols */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          {/* Warranty card */}
          <DetailCard
            title="Warranty"
            icon={<ShieldCheck className="h-3.5 w-3.5" />}
            collapsible={false}
            trailing={warranty ? (
              <span
                className={`inline-flex items-center gap-1.5 rounded-full border-[0.5px] px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] ${
                  isActive
                    ? 'border-[var(--green-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]'
                    : 'border-[var(--status-error-border,rgba(153,27,27,0.2))] bg-[var(--status-error-bg)] text-[var(--status-error)]'
                }`}
              >
                <ShieldCheck className="h-3 w-3" />
                {isActive ? 'Active' : 'Expired'}
              </span>
            ) : undefined}
          >
            {warranty ? (
              <>
                {/* Warranty name + provider + view certificate */}
                <div className="border-b-[0.5px] border-b-[var(--rule-light)] px-4 py-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="mb-0.5 text-[14px] text-[var(--ink)]">
                        {warranty.class_name}
                      </p>
                      {holding.organisation.logo_url ? (
                        <img
                          src={holding.organisation.logo_url}
                          alt={holding.organisation.name}
                          className="mt-1 h-4 max-w-[80px] object-contain opacity-60"
                        />
                      ) : (
                        <p className="text-[11px] text-[var(--ink-3)]">
                          {holding.organisation.name}
                        </p>
                      )}
                    </div>
                    {holding.certificate_download_enabled && (
                      <Button
                        variant="link"
                        type="button"
                        onClick={handleViewCertificate}
                        className="mt-0.5 h-auto shrink-0 gap-1.5 p-0 text-[10px] text-[var(--green)] hover:underline"
                      >
                        <FileText className="h-3 w-3" />
                        View Certificate
                      </Button>
                    )}
                  </div>
                </div>

                <FieldRow label="Warranty" value={warranty.option_display_name} />
                <FieldRow label="Duration" value={warranty.term} />
                <FieldRow label="Start date" value={formatDate(warranty.active_from)} />
                <FieldRow
                  label="End date"
                  value={warranty.is_lifetime ? 'Lifetime' : formatDate(warranty.expires_at)}
                />

                {isActive && (
                  <div className="px-4 py-2.5">
                    <div className="rounded-[4px] bg-[var(--green-bg)] px-3 py-2 text-[11px] text-[var(--green)]">
                      {warranty.remaining_time_string} remaining
                    </div>
                  </div>
                )}

                {warranty.documents && warranty.documents.length > 0 && (
                  <div className="flex flex-col gap-1.5 border-t-[0.5px] border-t-[var(--rule-light)] px-4 py-3">
                    {warranty.documents.map((doc) => (
                      <a
                        key={doc.uuid}
                        href={doc.url ?? '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[12px] text-[var(--green)] hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {doc.label}
                      </a>
                    ))}
                  </div>
                )}

              </>
            ) : (
              <div className="px-4 py-4 text-[12px] text-[var(--ink-3)]">
                No warranty associated with this product
              </div>
            )}
          </DetailCard>

          {/* Claims */}
          {holdingClaims.length > 0 && (
            <DetailCard
              title="Claims"
              icon={<ClipboardList className="h-3.5 w-3.5" />}
              collapsible={false}
            >
              <div className="flex flex-col gap-1 p-3">
                {holdingClaims.map((claim) => {
                  const hasNotification =
                    claim.has_pending_consumer_action || claim.has_unread_notification
                  return (
                    <Button
                      key={claim.uuid}
                      variant="ghost"
                      onClick={() => navigate(`/claims/${claim.uuid}`)}
                      className="flex items-center justify-between w-full h-auto px-4 py-3 rounded-[4px] font-normal text-left cursor-pointer bg-[var(--bg-subtle)] hover:bg-[var(--green-bg)] transition-colors"
                    >
                      <div>
                        <p className="text-[12px] text-[var(--ink)]">
                          {claim.claim_schema_name || 'Claim'}
                        </p>
                        <p className="text-[10px] text-[var(--ink-3)]">
                          {claim.created_at ? formatDate(claim.created_at) : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {hasNotification && (
                          <span
                            className="h-2 w-2 shrink-0 rounded-full bg-[var(--status-error)]"
                            aria-label={t('claims.actionNeeded', 'Action needed on this claim')}
                            title={t('claims.actionNeeded', 'Action needed on this claim')}
                          />
                        )}
                        {claim.claim_state && (
                          <span className="text-[10px] uppercase tracking-[0.06em] text-[var(--ink-3)]">
                            {claim.claim_state}
                          </span>
                        )}
                      </div>
                    </Button>
                  )
                })}
              </div>
            </DetailCard>
          )}

          {/* Actions */}
          <DetailCard
            title="Actions"
            icon={<Zap className="h-3.5 w-3.5" />}
            collapsible={false}
          >
            <div className="flex flex-col gap-2 p-3">
              {claimsEnabled && (
                <Button
                  variant="ghost"
                  onClick={() => navigate(`/products/${uuid}/submit-claim`)}
                  className="flex items-center justify-start gap-3 w-full px-4 py-3 rounded-[4px] text-[12px] transition-colors cursor-pointer text-left bg-[var(--bg-subtle)] hover:bg-[var(--green-bg)] text-[var(--ink)]"
                  data-testid="consumer-submit-claim"
                >
                  <AlertTriangle className="h-4 w-4 text-[var(--green)]" />
                  {t('productDetail.submitClaim', 'Submit a Claim')}
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => navigate('/account')}
                className="flex items-center justify-start gap-3 w-full px-4 py-3 rounded-[4px] text-[12px] transition-colors cursor-pointer text-left bg-[var(--bg-subtle)] hover:bg-[var(--green-bg)] text-[var(--ink)]"
              >
                <Pencil className="h-4 w-4 text-[var(--green)]" />
                {t('productDetail.editMyDetails', 'Edit my details')}
              </Button>
            </div>
          </DetailCard>
        </div>
      </div>
      <CertificateViewerDialog
        open={certOpen}
        onOpenChange={setCertOpen}
        html={certHtml}
      />
    </ConsumerShell>
  );
}
